import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, AlertCircle, CheckCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { useLanguage } from 'hooks/useLanguage';
import Button from 'components/ui/Button';
import PersonalInfoStep from './components/PersonalInfoStep';
import AccountPreferencesStep from './components/AccountPreferencesStep';
import VerificationStep from './components/VerificationStep';
import ProgressIndicator from './components/ProgressIndicator';

const RegisterScreen = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    // Account Preferences
    tradingExperience: '',
    accountType: 'individual',
    baseCurrency: 'USD',
    agreedToTerms: false,
    agreedToPrivacy: false,
    // Verification
    idDocument: null,
    proofOfAddress: null
  });

  const steps = [
    { number: 1, title: t('Personal Information'), completed: false },
    { number: 2, title: t('Account Preferences'), completed: false },
    { number: 3, title: t('Verification'), completed: false }
  ];

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return validatePersonalInfo();
      case 2:
        return validateAccountPreferences();
      case 3:
        return validateVerification();
      default:
        return false;
    }
  };

  const validatePersonalInfo = () => {
    const { firstName, lastName, email, phone, password, confirmPassword } = formData;
    
    if (!firstName?.trim()) {
      setError(t('First name is required'));
      return false;
    }
    if (!lastName?.trim()) {
      setError(t('Last name is required'));
      return false;
    }
    if (!email?.trim()) {
      setError(t('Email is required'));
      return false;
    }
    if (!/\S+@\S+\.\S+/?.test(email)) {
      setError(t('Please enter a valid email address'));
      return false;
    }
    if (!phone?.trim()) {
      setError(t('Phone number is required'));
      return false;
    }
    if (!password?.trim()) {
      setError(t('Password is required'));
      return false;
    }
    if (password?.length < 8) {
      setError(t('Password must be at least 8 characters'));
      return false;
    }
    if (password !== confirmPassword) {
      setError(t('Passwords do not match'));
      return false;
    }
    return true;
  };

  const validateAccountPreferences = () => {
    const { tradingExperience, agreedToTerms, agreedToPrivacy } = formData;
    
    if (!tradingExperience) {
      setError(t('Please select your trading experience level'));
      return false;
    }
    if (!agreedToTerms) {
      setError(t('You must agree to the Terms of Service'));
      return false;
    }
    if (!agreedToPrivacy) {
      setError(t('You must agree to the Privacy Policy'));
      return false;
    }
    return true;
  };

  const validateVerification = () => {
    const { idDocument, proofOfAddress } = formData;
    
    if (!idDocument) {
      setError(t('ID document is required'));
      return false;
    }
    if (!proofOfAddress) {
      setError(t('Proof of address is required'));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      // Mark current step as completed
      steps[currentStep - 1].completed = true;
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleRegister = async () => {
    if (!validateStep(3)) return;

    setIsLoading(true);
    setError('');

    try {
      // Create FormData for file uploads
      const registrationData = new FormData();
      
      // Add form fields
      Object.keys(formData)?.forEach(key => {
        if (key === 'idDocument' || key === 'proofOfAddress') {
          if (formData?.[key]) {
            registrationData?.append(key, formData?.[key]);
          }
        } else {
          registrationData?.append(key, formData?.[key]);
        }
      });

      const response = await fetch(`${import.meta.env?.VITE_ZONEX_API_URL || 'https://manager.zonextrader.com/api'}/auth/register`, {
        method: 'POST',
        body: registrationData
      });

      const data = await response?.json();

      if (!response?.ok) {
        throw new Error(data.message || t('Registration failed'));
      }

      setSuccess(t('Registration successful! Please check your email to verify your account.'));
      
      // Redirect to email verification page after 3 seconds
      setTimeout(() => {
        navigate('/email-verification', { 
          state: { 
            email: formData?.email,
            message: t('Please check your email and click the verification link.')
          }
        });
      }, 3000);

    } catch (error) {
      console.error('Registration error:', error);
      setError(error?.message || t('Registration failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInfoStep
            formData={formData}
            onInputChange={handleInputChange}
            error={error}
          />
        );
      case 2:
        return (
          <AccountPreferencesStep
            formData={formData}
            onInputChange={handleInputChange}
            error={error}
          />
        );
      case 3:
        return (
          <VerificationStep
            formData={formData}
            onInputChange={handleInputChange}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-slate-900 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white rounded-full p-3">
              <UserPlus className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white">
            {t('Create Your Zonex Account')}
          </h1>
          <p className="text-blue-100 mt-2">
            {t('Join the professional trading platform')}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="p-6 border-b">
          <ProgressIndicator steps={steps} currentStep={currentStep} />
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Status Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-green-700 text-sm">{success}</span>
            </div>
          )}

          {/* Step Content */}
          <div className="mb-6">
            {renderStepContent()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between gap-4">
            <Button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              variant="outline"
              className="flex-1 sm:flex-none sm:min-w-[120px] flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('Previous')}
            </Button>

            {currentStep < 3 ? (
              <Button
                type="button"
                onClick={handleNext}
                className="flex-1 sm:flex-none sm:min-w-[120px] bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white flex items-center justify-center gap-2"
              >
                {t('Next')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleRegister}
                disabled={isLoading}
                className="flex-1 sm:flex-none sm:min-w-[120px] bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white flex items-center justify-center gap-2"
              >
                {isLoading ? t('Creating Account...') : t('Create Account')}
              </Button>
            )}
          </div>

          {/* Login Link */}
          <div className="text-center mt-6 pt-6 border-t border-gray-200">
            <p className="text-gray-600">
              {t('Already have an account?')}{' '}
              <Link 
                to="/login-screen" 
                className="text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
              >
                {t('Sign in here')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterScreen;